import { GameTree } from './types';
import Tree from '@sabaki/immutable-gametree';
import { DrawingLine } from '@/entities/game-board/canvas/model/types';

export const parseCoordinates = (coord: string): [number, number] => {
    if (coord === '' || coord === undefined) {
        return [-1, -1];
    }

    if (coord.length !== 2) {
        throw new Error('Неправильный формат координат: ожидается две буквы.');
    }

    const x = coord[0].charCodeAt(0) - 'a'.charCodeAt(0);
    const y = coord[1].charCodeAt(0) - 'a'.charCodeAt(0);

    return [x, y];
};

export const formatCoordinates = (coords: [number, number]): string => {
    if (coords.length !== 2 || typeof coords[0] !== 'number' || typeof coords[1] !== 'number') {
        throw new Error('Неправильный формат координат: ожидается массив из двух чисел.');
    }

    const [x, y] = coords;

    let col = '';
    let row = '';

    if (x >= 0) col = String.fromCharCode('a'.charCodeAt(0) + x);
    if (y >= 0) row = String.fromCharCode('a'.charCodeAt(0) + y);

    return `${col}${row}`;
};

const COORD = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T'];

export const getCoordinates = (coords: [number, number], boardSize?: number) => {
    if (coords.length !== 2 || typeof coords[0] !== 'number' || typeof coords[1] !== 'number') {
        throw new Error('Неправильный формат координат: ожидается строка из двух символов.');
    }

    if (!boardSize) {
        throw new Error('Не указан размер доски.');
    }

    const [x, y] = coords;

    if (x < 0 || y < 0) {
        return 'Пас';
    }

    return `${COORD[x]}${boardSize - y}`;
};

const regex = /([BW])\s*[,+]\s*([^\s]*)/gi;

export const resultGame = (input: string) => {
    const result = input.replace(regex, (match, prefix, value) => {
        if (value === 'R' || value === 'T') {
            return match;
        }

        if (/[\u00BC-\u00BE\u2150-\u215E]/.test(value)) {
            return match;
        }

        const isNumber = /^(\d+([.,]\d*)?|\d*[.,]\d+)$/.test(value);
        if (isNumber) {
            const num = parseFloat(value.replace(',', '.'));
            if (isNaN(num)) {
                return `${prefix}+${value}`;
            }

            if (Number.isInteger(num)) {
                return `${prefix}+${num}`;
            }

            const intPart = Math.floor(num);
            const frac = num - intPart;
            const epsilon = 0.001;

            if (Math.abs(frac - 0.5) < epsilon) {
                return intPart === 0 ? `${prefix}+½` : `${prefix}+${intPart}½`;
            }
            if (Math.abs(frac - 0.25) < epsilon) {
                return intPart === 0 ? `${prefix}+¼` : `${prefix}+${intPart}¼`;
            }
            if (Math.abs(frac - 0.75) < epsilon) {
                return intPart === 0 ? `${prefix}+¾` : `${prefix}+${intPart}¾`;
            }

            let formatted = num.toFixed(1);
            if (formatted.endsWith('.0')) {
                formatted = formatted.slice(0, -2);
            }
            return `${prefix}+${formatted}`;
        }

        const firstChar = value.charAt(0);
        if (firstChar === 'R' || firstChar === 'T') {
            return `${prefix}+${firstChar}`;
        }

        return `${prefix}+${value}`;
    });

    return result;
};

export const downloadSGF = (content: string, filename: string = 'game.sgf') => {
    const blob = new Blob([content], { type: 'application/x-go-sgf' });

    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `${filename}`;

    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

export const serializeDrawingLine = (line: DrawingLine): string =>
    `[${line.points.join(',')}|${line.color}|${line.width}]`;

function toSgfCoord(r: number, c: number) {
    const letter = (n: number) => String.fromCharCode('a'.charCodeAt(0) + n);
    return letter(c) + letter(r);
}

const SERVER_METADATA_TO_SGF: Record<string, string> = {
    label: 'LB',
    circle: 'CR',
    triangle: 'TR',
    square: 'SQ',
    'smile-up': 'U',
    'smile-down': 'D',
};

function convertServerMetadata(metadata: Record<string, unknown> | undefined): Record<string, string[]> {
    const properties: Record<string, string[]> = {};

    for (const [key, rawValue] of Object.entries(metadata ?? {})) {
        const sgfProperty = SERVER_METADATA_TO_SGF[key];
        if (!sgfProperty || typeof rawValue !== 'string') continue;
        try {
            properties[sgfProperty] = JSON.parse(rawValue);
        } catch {
            // Ignore malformed optional metadata and keep the position usable.
        }
    }

    return properties;
}

// Рекурсивная вставка нод в draft
function insertServerNode(draft: Parameters<Parameters<GameTree['mutate']>[0]>[0], parentId: number, node: any) {
    const colorProp = node.mv.col === 1 ? 'B' : 'W';
    const sgfCoord = toSgfCoord(node.mv.r, node.mv.c);

    const newId = draft.UNSAFE_appendNodeWithId(
        parentId,
        node.id,
        {
            ...(node.setup ?? { [colorProp]: [sgfCoord] }),
            ...convertServerMetadata(node.md),
        },
        { disableMerging: true }
    );

    if (!newId) {
        throw new Error(`Не удалось вставить ноду ID ${node.id}`);
    }

    for (const child of node.ch || []) {
        insertServerNode(draft, node.id, child);
    }
}

// Преобразуем дерево JSON → GameTree
export function convertServerTree(serverRootNode: any): GameTree {
    const getId = (
        (id) => () =>
            id++
    )(0);
    const tree: GameTree = new Tree({ getId });

    return tree.mutate((draft) => {
        const rootMetadata: Record<string, string[]> = {
            ...convertServerMetadata(serverRootNode.md),
            ...serverRootNode.setup,
        };
        for (const [property, values] of Object.entries(rootMetadata)) {
            values.forEach((value) => draft.addToProperty(draft.root.id, property, value));
        }
        for (const child of serverRootNode.ch || []) {
            insertServerNode(draft, draft.root.id, child);
        }
    });
}
